// Role Configuration - UNIFIED APPROACH
// Manages user roles and permissions (Admin, Manager, User, Quality Officer, etc.)
// Based on Prisma Role model schema

import { createTrackingConfig } from '../../components/generic/labels';
import { tableConfig, uiConfig } from '../../config/appConfig';

export const roleConfig = {
  entityName: 'Role',
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/role',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      // Role specific endpoints
      activate: (id) => `/${id}/activate`,
      deactivate: (id) => `/${id}/deactivate`,
      reorder: '/reorder'
    },
    lookupEndpoints: {
      // Roles typically don't have lookup dependencies
      // but you could add role categories, permission levels, etc.
    }
  },
  
  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: '700px', // Slightly larger for roles (permissions fields)
    defaultPageSize: tableConfig.defaultPageSize, // From app config: 10
    pageSizeOptions: tableConfig.pageSizeOptions, // From app config: [10, 25, 50, 100]
    defaultTableDensity: tableConfig.defaultDensity, // From app config: 'normal'
    requiredFieldStyle: uiConfig.requiredFieldStyle // From app config: 'badge'
  },
  
  // Service configuration
  service: {
    name: 'roleService',
    methods: {
      getAll: 'getAllRoles',
      getById: 'getRoleById', 
      create: 'createRole',
      update: 'updateRole',
      delete: 'deleteRole',
      validate: 'validateData'
    }
  },

  // UNIFIED FIELD CONFIGURATION
  fields: [
    // Basic Information
    { 
      name: 'id', 
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    { 
      name: 'name', 
      label: 'Role Name', 
      type: 'text', 
      required: true,
      placeholder: 'Enter role name (e.g., Quality Manager)',
      table: { header: 'Role Name', width: '200px', sortable: true },
      filterable: true,
      validation: { 
        required: true, 
        minLength: 2, 
        maxLength: 100,
        pattern: /^[A-Za-z\s&\-'.()]+$/,
        message: 'Role name must contain only letters, spaces, and common punctuation'
      }
    },
    { 
      name: 'description', 
      label: 'Description', 
      type: 'textarea', 
      required: false,
      placeholder: 'Describe the role responsibilities and scope',
      table: { header: 'Description', width: '300px', sortable: false },
      filterable: true,
      validation: { 
        required: false, 
        maxLength: 500
      },
      rows: 3,
      helperText: 'Brief description of role responsibilities and access level'
    },
    { 
      name: 'isActive', 
      label: 'Active', 
      type: 'switch', 
      required: false,
      defaultValue: true,
      table: { 
        header: 'Status', 
        width: '100px', 
        sortable: true,
        cellRenderer: 'boolean'
      },
      filterable: true,
      validation: { required: false },
      switchLabel: 'Role is active and available for assignment',
      description: 'Inactive roles cannot be assigned to new users'
    },
    { 
      name: 'isSystemRole', 
      label: 'System Role', 
      type: 'switch', 
      required: false,
      defaultValue: false,
      table: { 
        header: 'System', 
        width: '100px', 
        sortable: true,
        cellRenderer: 'boolean'
      },
      filterable: true,
      validation: { required: false },
      switchLabel: 'This is a system-defined role',
      description: 'System roles have predefined permissions and cannot be deleted'
    },
    { 
      name: 'record_order', 
      label: 'Display Order', 
      type: 'number', 
      required: false,
      placeholder: 'Enter display order (optional)',
      table: { header: 'Order', width: '80px', sortable: true },
      filterable: false,
      validation: { 
        required: false,
        min: 1,
        max: 999,
        integer: true
      },
      helperText: 'Controls the order in dropdown lists and displays'
    },
  ],

  // Table Configuration - Dynamic getter that generates columns from fields
  get tableColumns() {
    return this.fields
      .filter(field => field.table !== false && !field.hidden)
      .map(field => ({
        key: field.name,
        header: field.table?.header || field.label,
        width: field.table?.width || 'auto',
        sortable: field.table?.sortable !== false,
        displayField: field.table?.displayField || field.name,
        cellRenderer: field.table?.cellRenderer || 'default'
      }));
  },

  // Filter Configuration
  filterableFields: ['name', 'description', 'isActive', 'isSystemRole'],

  // Filter Groups for Basic/Advanced tabs
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic Filters',
      fields: ['name', 'isActive']
    },
    {
      name: 'advanced',
      label: 'Advanced Filters',
      fields: ['description', 'isSystemRole', 'record_order', 'color']
    }
  ],

  // Custom Sort Orders
  customSortOrders: {
    isActive: [true, false], // Active roles first
    isSystemRole: [true, false], // System roles first
    record_order: 'asc' // Ascending order by default
  },

  // Messages Configuration
  messages: {
    loading: 'Loading roles...',
    noData: 'No roles found. Click "Add Role" to create the first role.',
    loadError: 'Failed to load roles',
    saveSuccess: 'Role saved successfully!',
    saveError: 'Failed to save role',
    deleteSuccess: 'Role deleted successfully!',
    deleteError: 'Failed to delete role',
    deleteConfirm: 'Are you sure you want to delete this role? This action cannot be undone and may affect users assigned to this role.',
    activateSuccess: 'Role activated successfully!',
    deactivateSuccess: 'Role deactivated successfully!',
    systemRoleWarning: 'System roles cannot be deleted or have their core permissions modified.'
  },

  // Form Layout Configuration
  layout: {
    mode: 'sections',
    sections: [
      {
        id: 'basic',
        title: 'Role Information',
        icon: '👤',
        columns: 2,
        fields: [
          { name: 'name' },
          { name: 'description' },
          { name: 'isActive' },
          { name: 'isSystemRole' },
          { name: 'record_order' },
          { name: 'color' }
        ]
      }
    ]
  },

  // Tracking Section Configuration
  trackingSection: createTrackingConfig('Role'),

  // Labels Configuration
  labels: {
    // Using predefined labels + custom overrides
    singular: 'Role',
    plural: 'Roles',
    addButton: 'Add Role',
    editButton: 'Edit Role',
    deleteButton: 'Delete Role',
    
    // Custom labels for roles
    pageTitle: 'Role Management',
    pageSubtitle: 'Manage user roles and their permissions',
    searchPlaceholder: 'Search roles by name or description...',
    
    // Form labels
    basicInfoSection: 'Role Information',
    
    // Action labels
    activateRole: 'Activate Role',
    deactivateRole: 'Deactivate Role',
    reorderRoles: 'Reorder Roles'
  },

  // Fields to remove during data preparation for submission (used by BaseService)
  fieldsToRemove: [
    // Remove system fields that should not be updated
    'id',
    'createdAt',
    'updatedAt', 
    'createdById',
    'updatedById',
    // Remove all relation fields
    'users',
    'permissions'
  ],

  // Export filename for data exports
  exportFileName: 'roles',

  // Validation function for real-time field validation (dynamically assigned)
  validateField: null // Will be set after validation module loads
};
